import moment, { Moment } from 'moment';
import 'moment/locale/fr';
import momentTimezone from 'moment-timezone';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { stringsRes } from '../resources/strings';
import { DAYS, SHORT_DAY_LENGTH, TIMEZONE_FROM_SERVER } from './constants';
import { capitalizeFirstLetter } from './strings';

const { moment: momentTz } = new AdapterMoment({ instance: momentTimezone });

const locale: string = window.navigator.language;
moment.locale(locale);

export function formatDateFromTimestamp(timestamp: number, customFormat = stringsRes.time.lettreFormat): any {
    if (!timestamp) {
        return '';
    }
    return capitalizeFirstLetter(momentTz.unix(timestamp).tz(TIMEZONE_FROM_SERVER).format(customFormat));
}

export function convertToServerTimeZone(date: moment.Moment): Moment {
    return date.tz(TIMEZONE_FROM_SERVER);
}

export function unix(timestamp: number): Moment {
    return momentTz.unix(timestamp);
}

export function getUnix(date: moment.Moment): number {
    return momentTz(date).unix();
}

export function addTimeToDate(
    nb: number,
    date: any = momentTz().tz(TIMEZONE_FROM_SERVER),
    time: moment.unitOfTime.DurationConstructor = 'm',
): any {
    return moment(date).add(nb, time as moment.unitOfTime.DurationConstructor);
}

export function removeTimeToDate(
    nb: number,
    date: any = momentTz().tz(TIMEZONE_FROM_SERVER),
    time: moment.unitOfTime.DurationConstructor = 'm',
): any {
    return moment(date).subtract(nb, time as moment.unitOfTime.DurationConstructor);
}

export function getRemainingTimeFromDates(date: Date, endDate: Date = new Date()): string {
    const diff: string = moment(date).from(endDate);
    if (date < endDate) {
        return `${stringsRes.promo.expired}`;
    }
    return capitalizeFirstLetter(diff);
}

export function isTodayOrNextDayAfter7PM(date: Moment): boolean {
    const now = moment();
    const today = now.clone().startOf('day');
    const tomorrow = today.clone().add(1, 'day').startOf('day');
    // const after7PMToday = now.hour() >= 19;
    const after7PMToday = now.hour() > 15 || (now.hour() === 15 && now.minute() >= 55);
    const dayAfterTomorrow = today.clone().add(2, 'day');

    return (
        (date.isSame(today, 'day') || (date.isSame(tomorrow, 'day') && after7PMToday)) &&
        !date.isSameOrAfter(dayAfterTomorrow)
    );
}

export function getJoText(date: Moment): string {
    const now = moment();
    const today = now.clone().startOf('day');

    return date.isSame(today, 'day') ? stringsRes.jo.asap : stringsRes.jo.schedule;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function roundNumber(nombre: number, roundAmount: number, up = true): number {
    const valeurArrondie = up ? Math.ceil(nombre) : Math.floor(nombre);

    // Ensuite, calculez le reste par rapport à roundAmount
    const reste = valeurArrondie % roundAmount;

    // Si le reste est déjà 0, le nombre est déjà un multiple de roundAmount
    if (reste === 0) {
        return valeurArrondie;
    }

    if (up) {
        const difference = roundAmount - reste;
        return valeurArrondie + difference;
    }
    return valeurArrondie - reste;
}

// Compute tMin (now -2h) and tMax (now + 12h)
export function getRideTrackingMinMaxTime(): { timestamp_min: number; timestamp_max: number } {
    const tMin = removeTimeToDate(2, undefined, 'h').format('X');
    const tMax = addTimeToDate(12, undefined, 'h').format('X');
    // Rounded
    // const tMin = roundNumber(removeTimeToDate(2, undefined, 'h').format('X'), 30, false);
    // const tMax = roundNumber(addTimeToDate(12, undefined, 'h').format('X'), 30, true);
    return { timestamp_min: tMin, timestamp_max: tMax };
}

export function dateToCronString(dateInput: string | Date | Moment, daysOfWeek: number[]): string {
    // Check if the input is a string, Moment or Date and create a new Date object if it is needed
    let date;

    if (typeof dateInput === 'string') {
        date = new Date(dateInput);
    } else if (moment.isMoment(dateInput)) {
        date = dateInput.toDate();
    } else {
        date = dateInput; // Assume it's a Date object
    }

    console.log({ dateInput, date });

    const minute = date.getMinutes();
    const hour = date.getHours();
    // const dayOfMonth = date.getDate();
    // const month = date.getMonth() + 1; // Months are zero-based in JavaScript
    // const dayOfWeek = date.getDay(); // Sunday = 0, Monday = 1, and so on

    // Join the days of week array into a comma-separated string
    const daysOfWeekString = daysOfWeek.join(',');

    return `${minute} ${hour} * * ${daysOfWeekString}`;
}

export function cronStringToDateAndDays(
    cronString: string,
    prefix = true,
    short = true,
    date = true,
    nbchar = SHORT_DAY_LENGTH,
): string {
    // Split the cron string into its components
    const [minute, hour, , , daysOfWeekString] = cronString.split(' ');

    // Create a Date object using the extracted components
    // const date = new Date();
    // date.setMinutes(minute);
    // date.setHours(hour);
    // date.setDate(dayOfMonth);
    // date.setMonth(month - 1); // Months are zero-based in JavaScript

    // Convert the days of week string to an array of day labels
    const daysOfWeekKeys = daysOfWeekString.split(',').map(Number);
    const dayLabels = daysOfWeekKeys
        .map((key) => {
            const day = DAYS.find((d) => d.key === key);
            return day ? (short ? day.value.slice(0, nbchar) : day.value) : null;
        })
        .filter(Boolean); // Filter out any null values

    // Ensure hour and minute have at least 2 characters
    const paddedHour = hour.padStart(2, '0');
    const paddedMinute = minute.padStart(2, '0');

    return `${prefix ? `${stringsRes.time.prefixRecurrence} ` : ''}${dayLabels.join(', ')}${
        date ? ` ${stringsRes.common.at} ${paddedHour}:${paddedMinute}` : ''
    }`;
}
